import { message } from 'antd'
import type { AiAgentMessage } from '../ai-agent.type'

async function handleCopyContent(msg: AiAgentMessage): Promise<void> {
  const html = msg.parsedHtml || msg.text
  const container = document.createElement('div')
  container.innerHTML = html
  const plainText = container.textContent || container.innerText || msg.text

  try {
    const hidden = document.createElement('div')
    hidden.setAttribute('contenteditable', 'true')
    hidden.style.position = 'fixed'
    hidden.style.left = '-9999px'
    hidden.style.opacity = '0'
    hidden.style.pointerEvents = 'none'
    hidden.style.userSelect = 'text'
    hidden.innerHTML = html
    document.body.appendChild(hidden)

    const range = document.createRange()
    range.selectNodeContents(hidden)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)

    const ok = document.execCommand('copy')

    selection?.removeAllRanges()
    document.body.removeChild(hidden)

    if (ok) {
      message.success('Copied to clipboard')
      return
    }
  } catch {
    // ignore and fallback
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ClipboardItemCtor = (window as any).ClipboardItem
    if (navigator.clipboard && typeof ClipboardItemCtor === 'function') {
      const item = new ClipboardItemCtor({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      })
      await navigator.clipboard.write([item])
      message.success('Copied to clipboard')
      return
    }
  } catch {
    // ignore and fallback
  }

  try {
    await navigator.clipboard.writeText(plainText)
    message.success('Copied to clipboard')
  } catch {
    const textArea = document.createElement('textarea')
    textArea.value = plainText
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    message.success('Copied to clipboard')
  }
}

async function copyTableToClipboard(tableId: string, buttonElement?: HTMLElement): Promise<void> {
  const table = document.getElementById(tableId)
  if (!table) {
    console.warn(`Table with ID ${tableId} not found`)
    return
  }

  const rows = Array.from(table.querySelectorAll('tr'))
  const tsvRows = rows.map((row) => {
    const cells = Array.from(row.querySelectorAll('th, td'))
    return cells.map((cell) => cell.textContent?.trim() || '').join('\t')
  })
  const tableData = tsvRows.join('\n')

  try {
    await navigator.clipboard.writeText(tableData)

    if (buttonElement) {
      const originalText = buttonElement.innerHTML
      buttonElement.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 6L9 17l-5-5"/>
        </svg>
        Copied!
      `
      setTimeout(() => {
        buttonElement.innerHTML = originalText
      }, 1000)
    }
    return
  } catch (err) {
    console.error('Failed to copy table:', err)
  }

  const textArea = document.createElement('textarea')
  textArea.value = tableData
  document.body.appendChild(textArea)
  textArea.select()
  try {
    document.execCommand('copy')
    if (buttonElement) {
      const originalText = buttonElement.innerHTML
      buttonElement.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 6L9 17l-5-5"/>
        </svg>
        Copied!
      `
      setTimeout(() => {
        buttonElement.innerHTML = originalText
      }, 1000)
    }
  } catch (fallbackErr) {
    console.error('Fallback copy also failed:', fallbackErr)
  }
  document.body.removeChild(textArea)
}

async function copyEntireChat(messages: AiAgentMessage[]): Promise<void> {
  if (!messages || messages.length === 0) {
    message.warning('No messages to copy')
    return
  }

  // Build HTML version with styles preserved
  const htmlMessages = messages.map((msg) => {
    const html = msg.parsedHtml || msg.text || ''
    const senderLabel = msg.sender === 'user' ? 'You' : 'AL'
    const timestamp = msg.timestamp
      ? new Date(msg.timestamp).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : ''

    return `
      <div style="margin-bottom: 16px;">
        <div style="font-weight: 600; color: #333; margin-bottom: 4px;">
          ${senderLabel}${
      timestamp
        ? ` <span style="font-weight: 400; color: #888; font-size: 0.9em;">(${timestamp})</span>`
        : ''
    }
        </div>
        <div style="color: #555; line-height: 1.5;">
          ${html}
        </div>
      </div>
    `
  })

  const fontFamily =
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
  const htmlContent = `
    <div style="font-family: ${fontFamily}; padding: 16px;">
      ${htmlMessages.join(
        '<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 16px 0;" />',
      )}
    </div>
  `

  // Build plain text version as fallback
  const formattedMessages = messages.map((msg) => {
    const container = document.createElement('div')
    container.innerHTML = msg.parsedHtml || msg.text || ''
    const plainText = container.textContent || container.innerText || msg.text || ''

    const senderLabel = msg.sender === 'user' ? 'You' : 'AL'
    const timestamp = msg.timestamp
      ? new Date(msg.timestamp).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : ''

    return `${senderLabel}${timestamp ? ` (${timestamp})` : ''}:\n${plainText.trim()}`
  })

  const plainText = formattedMessages.join('\n\n---\n\n')

  // Try to copy HTML with styles first
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ClipboardItemCtor = (window as any).ClipboardItem
    if (navigator.clipboard && typeof ClipboardItemCtor === 'function') {
      const item = new ClipboardItemCtor({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      })
      await navigator.clipboard.write([item])
      message.success('Chat copied to clipboard')
      return
    }
  } catch {
    // Fallback to execCommand with HTML
  }

  // Try execCommand with HTML content
  try {
    const hidden = document.createElement('div')
    hidden.setAttribute('contenteditable', 'true')
    hidden.style.position = 'fixed'
    hidden.style.left = '-9999px'
    hidden.style.opacity = '0'
    hidden.style.pointerEvents = 'none'
    hidden.style.userSelect = 'text'
    hidden.innerHTML = htmlContent
    document.body.appendChild(hidden)

    const range = document.createRange()
    range.selectNodeContents(hidden)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)

    const ok = document.execCommand('copy')

    selection?.removeAllRanges()
    document.body.removeChild(hidden)

    if (ok) {
      message.success('Chat copied to clipboard')
      return
    }
  } catch {
    // Fallback to plain text
  }

  // Final fallback: plain text
  try {
    await navigator.clipboard.writeText(plainText)
    message.success('Chat copied to clipboard')
  } catch {
    const textArea = document.createElement('textarea')
    textArea.value = plainText
    textArea.style.position = 'fixed'
    textArea.style.left = '-9999px'
    textArea.style.opacity = '0'
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    message.success('Chat copied to clipboard')
  }
}

export { handleCopyContent, copyTableToClipboard, copyEntireChat }
